<template>
  <BaseWidget 
    title="Top accounts by revenue"
    :show-info-icon="true"
    info-tooltip="Accounts ranked by total revenue within the selected date range."
    :edit-mode="editMode"
    :widget-id="props.widgetId"
    :date-range="dateRange"
    :past-only="true"
    @update:date-range="handleDateRangeChange"
    @remove-widget="$emit('remove-widget', props.widgetId)"
  >
    <div
      v-if="loading"
      class="loading-state"
    >
      <SkeletonLoader
        type="widget"
        :show-table="true"
        :compact="focused"
      />
    </div>
    <div
      v-else-if="error"
      class="error-state"
    >
      <RateLimitError v-if="isRateLimitError(error)" />
      <span v-else>Error: {{ error.message }}</span>
    </div>
    <div
      v-else
      class="widget-content-wrapper"
    >
      <div class="total-revenue-metric">
        <div class="metric-value">
          {{ totalRevenueFormatted }}
        </div>
        <div class="metric-label">
          Total Revenue
        </div>
      </div>
      <div class="accounts-table">
        <div class="table-header">
          <div class="table-header-cell account-cell">
            Account
          </div>
          <div class="table-header-cell revenue-cell">
            Revenue
          </div>
        </div>
        <div
          v-if="accounts.length === 0"
          class="no-accounts"
        >
          No accounts found for this period
        </div>
        <div 
          v-for="account in accounts.slice(0, 3)"
          :key="account.id" 
          class="table-row clickable"
          @click="handleAccountClickGuarded(account)"
        >
          <div class="table-cell account-cell">
            <div class="account-info">
              <div class="account-avatar">
                <img 
                  v-if="account.avatar" 
                  :src="account.avatar" 
                  :alt="account.displayName || ''"
                >
                <div
                  v-else
                  class="avatar-placeholder"
                >
                  {{ getInitials(account.fullName) }}
                </div>
              </div>
              <div class="account-name">
                {{ account.displayName || 'Unknown Account' }}
              </div>
            </div>
          </div>
          <div class="table-cell revenue-cell">
            <div class="revenue-amount">
              {{ account.revenueFormatted }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </BaseWidget>
</template>

<script setup>
import { computed, onUnmounted, ref, watch } from 'vue'
import BaseWidget from './BaseWidget.vue'
import SkeletonLoader from './SkeletonLoader.vue'
import RateLimitError from './RateLimitError.vue'
import { useTopAccountsByRevenueData } from '../composables/useWidgetData.js'
import { isRateLimitError } from '../utils/errorUtils.js'
import { getAvatarUrl } from '../utils/avatar.js'
import { useTrialModeGuard } from '../composables/useTrialModeGuard.js'

const props = defineProps({
  rowSpan: {
    type: Number,
    default: 1
  },
  colSpan: {
    type: Number,
    default: 1
  },
  editMode: {
    type: Boolean,
    default: false
  },
  props: {
    type: Object,
    default: () => ({})
  },
  widgetId: {
    type: String,
    default: null
  },
  sectionId: {
    type: String,
    default: null
  },
  widgetType: {
    type: String,
    default: null
  },
  focused: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['update:props', 'remove-widget'])

const dateRange = ref(props.props?.dateRange || 'last_30_days')
const limit = ref(props.props?.limit || 10)

const { data, loading, error, cleanup } = useTopAccountsByRevenueData({
  variables: computed(() => ({
    limit: limit.value
  })),
  priority: 1
})

// Helper function to normalize locale format (convert en_CA to en-CA)
const normalizeLocale = (locale) => {
  if (!locale) return 'en-US'
  return locale.replace(/_/g, '-')
}

// Helper function to format name as "Firstname L."
const formatName = (fullName) => {
  if (!fullName) return 'Unknown'
  const parts = fullName.trim().split(/\s+/)
  if (parts.length === 0) return 'Unknown'
  if (parts.length === 1) return parts[0]
  // First name + first letter of last name
  const firstName = parts[0]
  const lastNameInitial = parts[parts.length - 1].charAt(0).toUpperCase()
  return `${firstName} ${lastNameInitial}.`
}

// Helper function to get initials for avatar placeholder
const getInitials = (name) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.charAt(0).toUpperCase()
}

// Process accounts data from analytics query response
const accounts = computed(() => {
  if (!data.value?.analyticsQuery?.data || !Array.isArray(data.value.analyticsQuery.data)) {
    return []
  }
  
  const currencySymbol = data.value?.analyticsQuery?.currency_symbol || '$'
  const rawLocale = data.value?.analyticsQuery?.locale || 'en-US'
  const locale = normalizeLocale(rawLocale)
  const currency = data.value?.analyticsQuery?.currency || 'USD'
  
  // The data array contains rows, where each row is an array of values
  // Based on the query structure:
  // - First column: account name (full name)
  // - Second column: revenue amount
  // - Third column (optional): avatar URL
  // - Fourth column (optional): account ID
  return data.value.analyticsQuery.data
    .map((row) => {
      if (!Array.isArray(row) || row.length < 2) {
        return null
      }
      
      const fullName = row[0] || 'Unknown Account'
      // Handle revenue - could be number or string
      let revenue = 0
      if (row[1] !== undefined && row[1] !== null) {
        revenue = typeof row[1] === 'number' ? row[1] : parseFloat(row[1])
        if (isNaN(revenue)) revenue = 0
      }
      const avatarUrl = row[2] || null // Avatar URL if available
      const accountId = row[3] || null // Account ID if available
      const avatar = getAvatarUrl(avatarUrl, fullName)
      
      // Format revenue
      let revenueFormatted
      try {
        revenueFormatted = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(revenue)
      } catch (error) {
        // Fallback to simple formatting if locale is invalid
        console.warn('Invalid locale format, using fallback:', rawLocale, error)
        revenueFormatted = `${currencySymbol}${revenue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
      }
      
      return {
        id: accountId || fullName, // Use account ID if available, otherwise fallback to name
        account_id: accountId, // For opening account panel
        fullName: fullName,
        displayName: formatName(fullName),
        avatar,
        revenue: revenue,
        revenueFormatted: revenueFormatted
      }
    })
    .filter(account => account !== null)
    .sort((a, b) => b.revenue - a.revenue) // Sort descending by revenue
})

// Calculate total revenue
const totalRevenue = computed(() => {
  return accounts.value.reduce((sum, account) => sum + account.revenue, 0)
})

// Format total revenue
const totalRevenueFormatted = computed(() => {
  if (!data.value?.analyticsQuery) return '$0'
  
  const currencySymbol = data.value.analyticsQuery.currency_symbol || '$'
  const rawLocale = data.value.analyticsQuery.locale || 'en-US'
  const locale = normalizeLocale(rawLocale)
  const currency = data.value.analyticsQuery.currency || 'USD'
  
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(totalRevenue.value)
  } catch (error) {
    return `${currencySymbol}${totalRevenue.value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
})

const getUrlParams = () => {
  const urlParams = new URLSearchParams(window.location.search)
  let clientId = urlParams.get('client_id') || urlParams.get('clientId') || ''
  let canvasUUID = urlParams.get('canvasUUID') || urlParams.get('canvas_uuid') || urlParams.get('canvasUuid') || ''
  if (document.referrer) {
    try {
      const referrerParams = new URLSearchParams(new URL(document.referrer).search)
      if (!clientId) clientId = referrerParams.get('client_id') || referrerParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = referrerParams.get('canvasUUID') || referrerParams.get('canvas_uuid') || referrerParams.get('canvasUuid') || ''
    } catch (_e) {}
  }
  if ((!clientId || !canvasUUID) && window.parent && window.parent !== window) {
    try {
      const parentParams = new URLSearchParams(new URL(window.parent.location.href).search)
      if (!clientId) clientId = parentParams.get('client_id') || parentParams.get('clientId') || ''
      if (!canvasUUID) canvasUUID = parentParams.get('canvasUUID') || parentParams.get('canvas_uuid') || parentParams.get('canvasUuid') || ''
    } catch (_e) {}
  }
  return { client_id: clientId, canvasUUID }
}

const { guard } = useTrialModeGuard()
const handleAccountClickGuarded = guard((account) => {
  const accountId = account.account_id ?? account.id
  if (!accountId || (typeof accountId === 'string' && !/^\d+$/.test(accountId))) return
  const params = getUrlParams()
  const message = {
    command: 'ViewDetails',
    payload: {
      account_id: String(accountId),
      canvasUUID: params.canvasUUID || 'xxx'
    }
  }
  try {
    window.parent.postMessage(message, '*')
    if (window.top && window.top !== window.parent) {
      window.top.postMessage(message, '*')
    }
  } catch (e) {
    console.error('Failed to open account panel:', e)
  }
})

const handleDateRangeChange = (newDateRange) => {
  if (newDateRange !== dateRange.value) {
    dateRange.value = newDateRange
    emit('update:props', { dateRange: newDateRange })
  }
}

// Watch for prop changes from parent (e.g., when loading from database)
watch(() => props.props?.dateRange, (v) => {
  if (v && v !== dateRange.value) dateRange.value = v
})

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped>
.widget-content-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 0;
}

.total-revenue-metric {
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #E0E0E0;
}

.metric-value {
  font-size: 28px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  line-height: 1.15;
  margin-top: -1px;
}

.metric-label {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
  margin-top: 4px;
}

.loading-state,
.error-state {
  padding: 20px;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
  font-size: 14px;
}

.error-state {
  color: var(--color-red);
}

.accounts-table {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 0;
}

.table-header {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid #E0E0E0;
  margin-bottom: 8px;
}

.table-header-cell {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.87);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.table-header-cell.account-cell {
  text-align: left;
}

.table-header-cell.revenue-cell {
  text-align: right;
}

.table-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 16px;
  padding: 12px 0;
  min-height: 64px;
  border-bottom: 1px solid #F5F5F5;
  transition: background-color 0.2s ease;
}

.table-row:first-child {
  border-top: 1px solid #F5F5F5;
}

.table-row:last-child {
  border-bottom: none;
}

.table-row.clickable {
  cursor: pointer;
}

.table-row:hover {
  background-color: rgba(0, 0, 0, 0.02);
  border-radius: 4px;
  margin: 0 -4px;
  padding-left: 4px;
  padding-right: 4px;
}

.table-cell {
  display: flex;
  align-items: center;
}

.table-cell.account-cell {
  justify-content: flex-start;
  min-width: 0;
}

.account-info {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.account-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
  background: #f0f0f0;
}

.account-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  font-weight: 600;
  font-size: 14px;
}

.table-cell.revenue-cell {
  justify-content: flex-end;
  flex-shrink: 0;
}

.account-name {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.revenue-amount {
  font-size: 13px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
  white-space: nowrap;
}

.no-accounts {
  padding: 40px 20px;
  text-align: center;
  color: #999;
  font-size: 14px;
}
</style>
